import type { ReactElement } from 'react';
import styled from 'styled-components';
import type { TabPill } from '../../../components';
import { TabPills } from '../../../components';
import type { ZendeskPriority } from '../zendesk';

/** Shape every `<select>` in the form consumes, whatever it was built from. */
export interface FieldOption {
  value: string;
  label: string;
}

/** Everything the user picks for a new ticket, minus the description notes. */
export interface TicketDraft {
  subject: string;
  /** Contact email. */
  requester: string;
  /** Assignee key from the `agents` action. */
  assignee: string;
  /** Issue-type value from the `fields` action. */
  issueType: string;
  priority: ZendeskPriority;
  serviceImpact: boolean;
}

interface Props {
  draft: TicketDraft;
  onChange: (patch: Partial<TicketDraft>) => void;
  requesterOptions: FieldOption[];
  assigneeOptions: FieldOption[];
  issueTypeOptions: FieldOption[];
}

const PRIORITIES: { key: ZendeskPriority; label: string }[] = [
  { key: 'low', label: 'Low' },
  { key: 'normal', label: 'Normal' },
  { key: 'high', label: 'High' }
];

// Reusable input label (avoids the global .label / .row styles).
export const FieldLabel = styled.div`
  font-weight: 600;
  font-size: 9px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(176, 186, 209, 0.5);
  margin-bottom: 6px;
`;

const TextInput = styled.input`
  width: 100%;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgb(240, 242, 245);
  padding: 9px 11px;
  font-weight: 600;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 11px;
  flex-wrap: wrap;
`;

const RowEnd = styled(Row)`
  align-items: flex-end;
`;

const Col = styled.div`
  flex: 1 1 0%;
  min-width: 150px;
`;

const PriorityCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/*
 * Service-impact toggle. A <label> wraps the box and both texts so clicking
 * anywhere on the row toggles it (and screen readers read the whole row).
 */
const ImpactRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin: 2px 0 4px;

  input {
    width: 15px;
    height: 15px;
    accent-color: rgb(78, 95, 253);
    cursor: pointer;
  }

  .name {
    font-weight: 600;
    font-size: 12px;
    color: rgb(213, 221, 238);
  }

  .hint {
    font-weight: 400;
    font-size: 11px;
    color: rgb(107, 122, 150);
  }
`;

const SelectField = styled.div`
  position: relative;

  select {
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    background: rgba(8, 14, 30, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: rgb(240, 242, 245);
    padding: 9px 30px 9px 11px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
  }

  select:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }

  option {
    background: rgb(13, 23, 51);
    color: rgba(213, 221, 238, 0.92);
  }

  option:hover,
  option:checked {
    background: #1967d2;
    color: rgb(255, 255, 255);
  }

  .chev {
    position: absolute;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.6);
  }
`;

// Subject, the three selects, priority and the service-impact toggle.
export const TicketFields = ({
  draft,
  onChange,
  requesterOptions,
  assigneeOptions,
  issueTypeOptions
}: Props): ReactElement => {
  const priorityPills: TabPill[] = PRIORITIES.map((item) => ({
    key: item.key,
    label: item.label,
    active: draft.priority === item.key,
    onClick: (): void => {
      onChange({ priority: item.key });
    }
  }));

  return (
    <>
      <div>
        <FieldLabel>Subject</FieldLabel>
        <TextInput
          type='text'
          placeholder='Subject for Ticket'
          value={draft.subject}
          onChange={(event): void => {
            onChange({ subject: event.target.value });
          }}
        />
      </div>

      <Row>
        <Col>
          <FieldLabel>Requester</FieldLabel>
          <SelectField>
            <select
              value={draft.requester}
              onChange={(event): void => {
                onChange({ requester: event.target.value });
              }}
            >
              <option value=''>Select contact…</option>
              {requesterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <i className='fas fa-chevron-down chev' />
          </SelectField>
        </Col>

        <Col>
          <FieldLabel>Ticket Assignee</FieldLabel>
          <SelectField>
            <select
              value={draft.assignee}
              onChange={(event): void => {
                onChange({ assignee: event.target.value });
              }}
            >
              {/* Blank on purpose: the ticket is unassigned until someone picks. */}
              <option value='' />
              {assigneeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <i className='fas fa-chevron-down chev' />
          </SelectField>
        </Col>
      </Row>

      <RowEnd>
        <Col>
          <FieldLabel>Issue Type</FieldLabel>
          <SelectField>
            <select
              value={draft.issueType}
              onChange={(event): void => {
                onChange({ issueType: event.target.value });
              }}
            >
              <option value=''>Select issue type…</option>
              {issueTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <i className='fas fa-chevron-down chev' />
          </SelectField>
        </Col>

        <PriorityCol>
          <FieldLabel>Priority</FieldLabel>
          <TabPills pills={priorityPills} darker />
        </PriorityCol>
      </RowEnd>

      <ImpactRow>
        <input
          type='checkbox'
          checked={draft.serviceImpact}
          onChange={(event): void => {
            onChange({ serviceImpact: event.target.checked });
          }}
        />
        <span className='name'>Service Impact</span>
        <span className='hint'>— the client&apos;s stream or service was affected</span>
      </ImpactRow>
    </>
  );
};
